import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export type Toggles = Record<string, boolean>;

const CalendarGroupContext = createContext<{
  toggles: Toggles;
  hydrated: boolean;
  setToggle: (group: string, value: boolean) => void;
  setAll: (next: Toggles) => void;
}>({
  toggles: {},
  hydrated: false,
  setToggle: () => {},
  setAll: () => {},
});

type ProviderProps = {
  children: React.ReactNode;
  groups: string[]; // pass Altersklassen here
};

export const CalendarGroupProvider: React.FC<ProviderProps> = ({ children, groups }) => {
  const [toggles, setToggles] = useState<Toggles>({});
  const [hydrated, setHydrated] = useState(false);

  // Build defaults: all current groups true (stable memo)
  const defaultExpanded = useMemo<Toggles>(() => {
    const acc: Toggles = {};
    for (const g of groups) acc[g] = true;
    return acc;
  }, [groups]);

  // Hydrate once (client-side), then merge defaults and persist
  useEffect(() => {
    // guard SSR
    if (typeof window === "undefined") return;

    let saved: Toggles | null = null;
    try {
      const raw = localStorage.getItem("calendarGroupToggles");
      if (raw) saved = JSON.parse(raw) as Toggles;
    } catch {
      saved = null;
    }

    const merged = { ...defaultExpanded, ...(saved ?? {}) };
    setToggles(merged);
    setHydrated(true);

    // write back only after we have merged
    try {
      localStorage.setItem("calendarGroupToggles", JSON.stringify(merged));
    } catch {
        // nothing 
    }
  }, [defaultExpanded]); // re-run if the groups list changes

  // Persist changes AFTER hydration (user interactions)
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem("calendarGroupToggles", JSON.stringify(toggles));
    } catch {}
  }, [toggles, hydrated]);

  const setToggle = (group: string, value: boolean) => {
    setToggles(prev => ({ ...prev, [group]: value }));
  };

  const setAll = (next: Toggles) => setToggles(next);

  return (
    <CalendarGroupContext.Provider value={{ toggles, hydrated, setToggle, setAll }}>
      {children}
    </CalendarGroupContext.Provider>
  );
};

export const useCalendarGroupToggles = () => useContext(CalendarGroupContext);